// src/features/admin/supportApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// ── Types ──────────────────────────────────────────────────────────────────────

// Backend support_tickets.status is an integer: 0=open 1=in_progress 2=resolved 3=closed
export type TicketStatusNum = 0 | 1 | 2 | 3;

export const STATUS_LABEL: Record<TicketStatusNum, string> = {
  0: 'Open',
  1: 'In Progress',
  2: 'Resolved',
  3: 'Closed',
};

export interface SupportTicket {
  id: number;
  subject: string;
  description: string;
  status: TicketStatusNum;
  order_id?: number | null;
  invoice_no?: string | null;
  created_at?: string;

  // who raised it
  is_guest: boolean;
  user_id?: number | null;
  guest_user_id?: string | null;

  // unified display fields — normalized in backend
  display_name: string;
  email: string;   // guest has no email → '-'
  phone: string;
}

export interface TicketReply {
  id?: number;
  message: string;
  created_at?: string;
  // The backend (`POST /user/support-conversation`) returns these per reply —
  // use sender_type to decide which side of the chat a bubble belongs on
  // instead of guessing from array index (that breaks as soon as either side
  // sends two messages in a row).
  sender_type?: 'admin' | 'customer';
  sender_name?: string;
  align?: 'left' | 'right';
}

export interface TicketConversation {
  ticket: {
    subject: string;
    status: TicketStatusNum;
  };
  replies: TicketReply[];
}

// ── Base query ────────────────────────────────────────────────────────────────

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_SERVER_ENDPOINT,
  prepareHeaders: (headers) => {
    const token = localStorage.getItem('admin_token');
    if (token) headers.set('Authorization', `Bearer ${token}`);
    headers.set(
      'authkey',
      '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3'
    );
    return headers;
  },
});

// ── API ────────────────────────────────────────────────────────────────────────

export const supportApi = createApi({
  reducerPath: 'supportApi',
  baseQuery,
  tagTypes: ['Ticket'],
  endpoints: (build) => ({

    // GET /admin/support-tickets
    // Lists all tickets with normalized user/guest details
    listAllTickets: build.query<{ success: boolean; tickets: SupportTicket[] }, void>({
      query: () => ({
        url: 'admin/support-tickets',
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }),
      providesTags: ['Ticket'],
    }),

    // POST /user/support-conversation  ← EXISTING route
    // Fetch full message thread for one ticket
    getConversation: build.query<TicketConversation, number>({
      query: (ticket_id) => ({
        url: 'user/support-conversation',
        method: 'POST',
        body: { ticket_id },
        headers: { 'Content-Type': 'application/json' },
      }),
      providesTags: (_result, _err, ticket_id) => [{ type: 'Ticket', id: ticket_id }],
    }),

    // POST /admin/support-reply  ← EXISTING route
    // Admin sends reply + updates status in one call
    sendReply: build.mutation<
      { success: boolean; message: string },
      { ticket_id: number; message: string; status?: TicketStatusNum }
    >({
      query: (body) => ({
        url: 'admin/support-reply',
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: (_result, _err, arg) => [
        'Ticket',
        { type: 'Ticket', id: arg.ticket_id },
      ],
    }),

    // POST /admin/support-update-status
    // Change status without sending a reply message
    updateTicketStatus: build.mutation<
      { success: boolean; message: string },
      { ticket_id: number; status: TicketStatusNum }
    >({
      query: (body) => ({
        url: 'admin/support-update-status',
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Ticket'],
    }),

  }),
});

export const {
  useListAllTicketsQuery,
  useGetConversationQuery,
  useSendReplyMutation,
  useUpdateTicketStatusMutation,
} = supportApi;